import React, { useEffect, useState } from "react";
import { Mail, Linkedin, ArrowDown, FileText, MapPin } from "lucide-react";
import { profileData, companyLogos } from "../mock";

const miniTimeline = [
  {
    key: "IIT Kanpur",
    color: "#0EA5A5",
    name: "IIT Kanpur",
    role: "B.Tech Mathematics",
    period: "2017–21",
    accentBg: "rgba(14,165,165,0.08)",
    accentBorder: "rgba(14,165,165,0.22)"
  },
  {
    key: "Goldman Sachs",
    color: "#5B9BD5",
    name: "Goldman Sachs",
    role: "Analyst",
    period: "2021–23",
    accentBg: "rgba(91,155,213,0.08)",
    accentBorder: "rgba(91,155,213,0.22)"
  },
  {
    key: "FRND",
    color: "#EC4899",
    name: "FRND",
    role: "Revenue Lead",
    period: "2023–25",
    accentBg: "rgba(236,72,153,0.08)",
    accentBorder: "rgba(236,72,153,0.22)"
  },
  {
    key: "GoodScore",
    color: "#10B981",
    name: "GoodScore",
    role: "Growth PM",
    period: "2025–Now",
    accentBg: "rgba(16,185,129,0.08)",
    accentBorder: "rgba(16,185,129,0.22)",
    current: true
  }
];

const OrgLogo = ({ orgKey, color, accentBg, accentBorder, size = 36 }) => {
  const [failed, setFailed] = useState(false);
  const cfg = companyLogos[orgKey];

  if (!cfg || failed) {
    return (
      <div
        className="flex items-center justify-center flex-shrink-0 rounded-lg overflow-hidden"
        style={{ width: size, height: size, background: accentBg, border: `1px solid ${accentBorder}` }}
      >
        <span className="text-xs font-bold" style={{ color }}>{orgKey.slice(0, 2).toUpperCase()}</span>
      </div>
    );
  }

  return (
    <div
      className="flex items-center justify-center flex-shrink-0 rounded-lg overflow-hidden"
      style={{ width: size, height: size, background: cfg.bg, border: `1px solid ${accentBorder}`, padding: cfg.padding }}
    >
      <img
        src={cfg.url}
        alt={orgKey}
        className={`w-full h-full ${cfg.cover ? "object-cover" : "object-contain"}`}
        onError={() => setFailed(true)}
      />
    </div>
  );
};

const HeroSection = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleScroll = () => {
    const el = document.querySelector("#experience");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden"
      style={{ background: "#09090B" }}
    >
      {/* Grid background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(6,182,212,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(6,182,212,0.03) 1px, transparent 1px)",
          backgroundSize: "64px 64px"
        }}
      />
      {/* Radial glow */}
      <div
        className="absolute top-1/3 right-0 w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(6,182,212,0.05) 0%, transparent 70%)",
          filter: "blur(60px)"
        }}
      />

      <div className="max-w-6xl mx-auto px-5 sm:px-6 pt-20 pb-14 relative z-10 w-full">

        {/* ── MOBILE LAYOUT (< md) ─────────────────── */}
        <div className="md:hidden">
          {/* Circular photo on mobile */}
          <div
            className={`flex justify-start mb-6 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          >
            <div className="relative">
              <div
                className="w-20 h-20 rounded-full overflow-hidden"
                style={{ border: "2px solid rgba(6,182,212,0.3)", boxShadow: "0 0 20px rgba(6,182,212,0.1)" }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_career-canvas-157/artifacts/nvlyykep_image.png"
                  alt="Aditya Sonthalia"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <span
                className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-[#09090B] bg-[#10B981]"
              />
            </div>
          </div>

          {/* Name */}
          <h1
            className={`text-4xl font-bold text-white leading-tight tracking-tight mb-2 transition-all duration-700 delay-75 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          >
            {profileData.name}
          </h1>

          {/* Tagline */}
          <p
            className={`text-sm leading-relaxed mb-7 transition-all duration-700 delay-100 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
            style={{ color: "#8A8A9A" }}
          >
            {profileData.tagline}
          </p>

          {/* Career path – 2×2 grid on mobile */}
          <div className={`mb-7 transition-all duration-700 delay-150 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: "#3F3F46" }}>Career path</p>
            <div className="grid grid-cols-2 gap-2">
              {miniTimeline.map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-xl"
                  style={{ background: item.accentBg, border: `1px solid ${item.accentBorder}` }}
                >
                  <OrgLogo orgKey={item.key} color={item.color} accentBg={item.accentBg} accentBorder={item.accentBorder} size={30} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-1">
                      <p className="text-xs font-semibold text-white leading-none truncate">{item.name}</p>
                      {item.current && <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse flex-shrink-0" />}
                    </div>
                    <p className="text-xs mt-0.5 leading-none truncate" style={{ color: item.color }}>{item.role}</p>
                    <p className="text-xs mt-0.5 leading-none" style={{ color: "#52525B" }}>{item.period}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Key areas */}
          <div className={`flex flex-wrap items-center gap-2 mb-6 transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            {profileData.keyAreas.map((area, i) => (
              <span key={i} className="px-2.5 py-1 text-xs font-semibold rounded-sm uppercase tracking-wider"
                style={{ background: "#141416", color: "#71717A", border: "1px solid #27272A" }}>{area}</span>
            ))}
          </div>

          {/* CTAs – full width on mobile */}
          <div className={`flex flex-col gap-3 mb-7 transition-all duration-700 delay-[250ms] ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <button
              onClick={handleScroll}
              className="w-full py-3 rounded-md text-sm font-semibold text-[#09090B] bg-[#06B6D4] hover:bg-[#0891B2] transition-colors duration-200 flex items-center justify-center gap-2"
            >
              View My Work <ArrowDown className="w-4 h-4" />
            </button>
            <a
              href={`mailto:${profileData.email}`}
              className="w-full py-3 rounded-md text-sm font-semibold text-white border border-[#27272A] hover:border-[#06B6D4]/50 transition-all duration-200 flex items-center justify-center gap-2"
            >
              <Mail className="w-4 h-4" /> Get in Touch
            </a>
          </div>

          {/* Social */}
          <div className={`flex items-center gap-5 transition-all duration-700 delay-300 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <a href={profileData.linkedin} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-sm text-[#52525B] hover:text-[#06B6D4] transition-colors duration-200">
              <Linkedin className="w-4 h-4" /> LinkedIn
            </a>
            <a href={profileData.resumeUrl} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-sm text-[#52525B] hover:text-[#06B6D4] transition-colors duration-200">
              <FileText className="w-4 h-4" /> Resume
            </a>
            <span className="flex items-center gap-1.5 text-sm text-[#52525B]">
              <MapPin className="w-4 h-4" /> Bengaluru
            </span>
          </div>
        </div>

        {/* ── DESKTOP LAYOUT (md+) ─────────────────── */}
        <div className="hidden md:grid grid-cols-[1fr_auto] gap-10 items-center">

          {/* LEFT */}
          <div>
            <h1
              className={`text-5xl lg:text-7xl font-bold text-white leading-tight tracking-tight mb-3 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
            >
              {profileData.name}
            </h1>

            <p
              className={`text-base lg:text-lg max-w-xl leading-relaxed mb-10 transition-all duration-700 delay-100 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
              style={{ color: "#8A8A9A" }}
            >
              {profileData.tagline}
            </p>

            {/* Desktop timeline – horizontal */}
            <div className={`mb-10 transition-all duration-700 delay-150 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: "#3F3F46" }}>Career path</p>
              <div className="flex flex-wrap items-center gap-2">
                {miniTimeline.map((item, i) => (
                  <React.Fragment key={i}>
                    <div
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-default transition-all duration-200 hover:scale-[1.03]"
                      style={{ background: item.accentBg, border: `1px solid ${item.accentBorder}` }}
                    >
                      <OrgLogo orgKey={item.key} color={item.color} accentBg={item.accentBg} accentBorder={item.accentBorder} size={36} />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-semibold text-white leading-none">{item.name}</p>
                          {item.current && <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse flex-shrink-0" />}
                        </div>
                        <p className="text-xs mt-0.5 leading-none" style={{ color: item.color }}>{item.role}</p>
                        <p className="text-xs mt-0.5 leading-none" style={{ color: "#52525B" }}>{item.period}</p>
                      </div>
                    </div>
                    {i < miniTimeline.length - 1 && (
                      <svg width="14" height="10" viewBox="0 0 14 10" fill="none" className="flex-shrink-0">
                        <path d="M0 5H10M10 5L7 2M10 5L7 8" stroke="#3F3F46" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Key areas */}
            <div className={`flex items-center gap-2.5 mb-8 transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              {profileData.keyAreas.map((area, i) => (
                <span key={i} className="px-3 py-1 text-xs font-semibold rounded-sm uppercase tracking-widest"
                  style={{ background: "#141416", color: "#71717A", border: "1px solid #27272A" }}>{area}</span>
              ))}
            </div>

            {/* CTAs */}
            <div className={`flex flex-wrap items-center gap-4 mb-8 transition-all duration-700 delay-[250ms] ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <button onClick={handleScroll}
                className="px-6 py-3 rounded-md text-sm font-semibold text-[#09090B] bg-[#06B6D4] hover:bg-[#0891B2] transition-colors duration-200 flex items-center gap-2">
                View My Work <ArrowDown className="w-4 h-4" />
              </button>
              <a href={`mailto:${profileData.email}`}
                className="px-6 py-3 rounded-md text-sm font-semibold text-white border border-[#27272A] hover:border-[#06B6D4]/50 hover:bg-white/5 transition-all duration-200 flex items-center gap-2">
                <Mail className="w-4 h-4" /> Get in Touch
              </a>
            </div>

            {/* Social */}
            <div className={`flex items-center gap-6 transition-all duration-700 delay-300 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <a href={profileData.linkedin} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-[#52525B] hover:text-[#06B6D4] transition-colors duration-200">
                <Linkedin className="w-4 h-4" /> LinkedIn
              </a>
              <a href={profileData.resumeUrl} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-[#52525B] hover:text-[#06B6D4] transition-colors duration-200">
                <FileText className="w-4 h-4" /> Resume
              </a>
              <span className="flex items-center gap-2 text-sm text-[#52525B]">
                <MapPin className="w-4 h-4" /> {profileData.location}
              </span>
            </div>
          </div>

          {/* RIGHT: Photo */}
          <div className={`flex justify-end items-center transition-all duration-1000 delay-200 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-6"}`}>
            <div className="relative">
              <div className="absolute inset-0 rounded-2xl pointer-events-none"
                style={{ background: "radial-gradient(circle at center, rgba(6,182,212,0.12) 0%, transparent 70%)", filter: "blur(24px)", transform: "scale(1.15)" }} />
              <div className="relative w-52 h-60 lg:w-64 lg:h-72 xl:w-72 xl:h-80 rounded-2xl overflow-hidden"
                style={{ border: "1px solid rgba(6,182,212,0.18)", boxShadow: "0 0 60px rgba(6,182,212,0.07)" }}>
                <img
                  src="https://customer-assets.emergentagent.com/job_career-canvas-157/artifacts/nvlyykep_image.png"
                  alt="Aditya Sonthalia"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute bottom-0 left-0 right-0 h-16"
                  style={{ background: "linear-gradient(to top, rgba(9,9,11,0.6), transparent)" }} />
              </div>
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5"
                style={{ background: "#141416", border: "1px solid #27272A", color: "#A1A1AA" }}>
                <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
                Growth PM · GoodScore
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce">
        <ArrowDown className="w-4 h-4 text-[#3F3F46]" />
      </div>
    </section>
  );
};

export default HeroSection;
